import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles, categories } from "@/mocks/gossip";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Seo from "@/components/feature/Seo";

const CATEGORY_FILTERS = [{ id: "all", name: "全部内容线" }, ...categories.map((item) => ({ id: item.id, name: item.name }))];

export default function HotList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const keyword = searchParams.get("q") ?? "";
  const [sort, setSort] = useState<"latest" | "heat">("latest");
  const [searchInput, setSearchInput] = useState(keyword);

  const filtered = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    const result = articles.filter((item) => {
      const matchCategory = activeCategory === "all" || item.category === activeCategory;
      const matchText =
        !text ||
        item.title.toLowerCase().includes(text) ||
        item.excerpt.toLowerCase().includes(text) ||
        item.tags.some((tag) => tag.toLowerCase().includes(text)) ||
        item.categoryLabel.toLowerCase().includes(text);
      return matchCategory && matchText;
    });
    return result.sort((a, b) => {
      if (sort === "heat") {
        return b.heat - a.heat;
      }
      return a.date < b.date ? 1 : -1;
    });
  }, [activeCategory, keyword, sort]);

  const activeCategoryName =
    categories.find((item) => item.id === activeCategory)?.name ?? "全部内容线";

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") {
        params.delete("category");
      } else {
        params.set("category", next.category);
      }
    }
    if (next.q !== undefined) {
      if (next.q.trim() === "") {
        params.delete("q");
      } else {
        params.set("q", next.q.trim());
      }
    }
    setSearchParams(params);
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: searchInput });
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "猛料总汇 - 看料吃瓜社",
      url: "https://kanliaochigua.example/hot",
      inLanguage: "zh-CN",
      description:
        "看料吃瓜猛料与大瓜总汇，支持按内容线筛选与关键词搜索，涵盖娱乐、网红、影视、音乐、电竞与时尚内容线。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliaochigua.example/" },
        { "@type": "ListItem", position: 2, name: "猛料总汇", item: "https://kanliaochigua.example/hot" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "看料吃瓜猛料列表",
      numberOfItems: filtered.length,
      itemListElement: filtered.slice(0, 10).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `https://kanliaochigua.example/hot/${item.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="猛料总汇 | 看料吃瓜大瓜与劲爆重料列表 - 看料吃瓜社"
        description="看料吃瓜猛料总汇，汇集看料吃瓜大瓜、劲爆重料与硬核整理内容。支持按娱乐、网红、影视、音乐、电竞、时尚六条内容线筛选，也可直接搜索关键词。"
        keywords="看料吃瓜猛料,看料吃瓜大瓜,看料吃瓜劲爆,看料吃瓜重料,猛料总汇,瓜料列表"
        path="/hot"
        jsonLd={jsonLd}
      />

      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "猛料总汇" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            猛料总汇：看料吃瓜大瓜与劲爆重料
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            当前浏览「{activeCategoryName}」，共 {filtered.length} 条瓜料。可以按内容线筛选，也可以用关键词搜索标题、摘要与标签。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full md:max-w-md" role="search">
              <label className="relative flex items-center flex-1">
                <i className="ri-search-line absolute left-3 text-foreground-400 text-sm pointer-events-none"></i>
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  type="search"
                  name="q"
                  placeholder="搜索瓜料标题、摘要或标签"
                  aria-label="搜索瓜料"
                  className="w-full pl-9 pr-3 py-2.5 text-sm font-label rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </label>
              <button
                type="submit"
                className="px-4 py-2.5 text-sm font-label font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="flex items-center gap-2">
              <span className="text-xs font-label text-foreground-500 whitespace-nowrap">排序</span>
              <div className="inline-flex p-1 rounded-full bg-background-100 border border-background-200">
                <button
                  type="button"
                  onClick={() => setSort("latest")}
                  className={`px-3 py-1.5 text-xs font-label rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sort === "latest" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  最新整理
                </button>
                <button
                  type="button"
                  onClick={() => setSort("heat")}
                  className={`px-3 py-1.5 text-xs font-label rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sort === "heat" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  讨论最热
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {CATEGORY_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => updateParams({ category: item.id })}
                className={`px-3.5 py-1.5 text-xs font-label rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === item.id
                    ? "bg-secondary-500 border-secondary-500 text-background-50"
                    : "bg-background-50 border-background-200 text-foreground-600 hover:border-secondary-300 hover:text-secondary-800"
                }`}
              >
                {item.name}
              </button>
            ))}
            {(activeCategory !== "all" || keyword) && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearchParams(new URLSearchParams());
                }}
                className="px-3 py-1.5 text-xs font-label text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            )}
          </div>

          {keyword && (
            <p className="text-xs font-label text-foreground-500">
              关键词「{keyword}」共匹配 {filtered.length} 条结果
            </p>
          )}
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-label text-foreground-500">
                已展示全部 {filtered.length} 条内容 · 累计讨论热度 {
                  filtered.reduce((sum, item) => sum + item.heat, 0).toLocaleString("zh-CN")
                }
              </p>
              <p className="text-xs font-label text-foreground-500">
                想看更长线的整理？前往
                <Link to="/topics" className="text-primary-700 hover:text-primary-800 ml-1">
                  专题分类
                </Link>
              </p>
            </div>
          </>
        ) : (
          <div className="py-20 text-center bg-background-50 border border-background-200 rounded-lg">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到匹配的瓜料
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              试着换一个关键词，或者切换内容线看看其他分类。
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setSearchParams(new URLSearchParams());
              }}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-label font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              查看全部瓜料
            </button>
          </div>
        )}
      </div>
    </>
  );
}